/*
 * Nightcap theme script (module, loaded once from snippets/nc-head.liquid).
 * Custom elements, so sections work again after the theme editor re-renders them:
 *   nc-turndown    the Turndown card hero: picking a person swaps the card, the photo, the box and the button
 *   nc-shop        zone chips dim the cards of other zones
 *   nc-reel        a clip plays in place on tap, one at a time
 *   nc-join-steps  two-step sign-up on the customer form
 *   nc-copy        copy a code
 * Guards: reduced motion, html.factory-lowfx (weak devices, Save-Data, in-app browsers) and the motion setting
 * (html.nc-anim) swap the hero at once without movement; hover effects only with a fine pointer; transform / opacity only.
 */

const root = document.documentElement;
const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
const motionOn = () => root.classList.contains('nc-anim') && !reduceQuery.matches && !root.classList.contains('factory-lowfx');

/* ------------------------------------------------------------------ Turndown card hero */
class NcTurndown extends HTMLElement {
  connectedCallback() {
    this.gift = this.querySelector('[data-gift]');
    this.cells = this.querySelector('[data-cells]');
    if (!this.gift || !this.cells) return;
    this.photos = [...this.querySelectorAll('[data-photos] img')];
    this.tag = this.querySelector('[data-photo-tag]');
    this.cta = this.querySelector('[data-cta]');
    this.busy = false;
    this.pending = null;
    const checked = this.querySelector('input[type="radio"]:checked');
    this.current = checked ? checked.value : '';
    // the first reveal is a CSS animation; once it has run, swaps use transitions
    const ready = () => this.gift.classList.add('is-ready');
    if (motionOn()) this.readyTimer = setTimeout(ready, 1300);
    else ready();
    this.onChange = (event) => {
      const input = event.target;
      if (input instanceof HTMLInputElement && input.type === 'radio' && input.checked) this.go(input.value);
    };
    this.addEventListener('change', this.onChange);
  }

  disconnectedCallback() {
    clearTimeout(this.readyTimer);
    this.removeEventListener('change', this.onChange);
  }

  data(key) {
    return this.querySelector(`template[data-who="${CSS.escape(key)}"]`);
  }

  paint(t) {
    const d = t.dataset;
    const to = this.gift.querySelector('[data-to]');
    if (to) to.textContent = d.to || '';
    this.gift.classList.toggle('is-long', (d.to || '').length > 6);
    const line = this.gift.querySelector('[data-line]');
    if (line) line.textContent = d.line || '';
    const boxTag = this.gift.querySelector('[data-box-tag]');
    if (boxTag) boxTag.textContent = d.box || '';
    const index = Number(d.photo) || 0;
    this.photos.forEach((img, i) => img.classList.toggle('is-on', i === index));
    if (this.tag) {
      this.tag.hidden = !d.tagName;
      if (d.tagName) {
        this.tag.textContent = `${d.tagName} `;
        const price = document.createElement('span');
        price.className = 'nc-num';
        price.textContent = d.tagPrice || '';
        this.tag.append(price);
        this.tag.href = d.tagHref || this.tag.href;
      }
    }
    if (this.cta) {
      const label = this.cta.querySelector('[data-cta-label]');
      const price = this.cta.querySelector('[data-cta-price]');
      if (label) label.textContent = d.cta || '';
      if (price) {
        price.textContent = d.price || '';
        price.hidden = !d.price;
      }
      if (d.href) this.cta.href = d.href;
    }
    const compare = this.querySelector('[data-cta-compare]');
    if (compare) compare.textContent = d.compare || '';
  }

  fill(t, animate) {
    const tiles = t.content.cloneNode(true);
    const count = tiles.querySelectorAll('.nc-tile').length;
    if (animate) tiles.querySelectorAll('.nc-tile').forEach((tile) => tile.classList.add('is-new'));
    this.cells.setAttribute('data-count', String(Math.min(count, 4)));
    this.cells.replaceChildren(tiles);
  }

  go(key) {
    if (key === this.current) return;
    const t = this.data(key);
    if (!t) return;
    if (this.busy) {
      this.pending = key;
      return;
    }
    this.current = key;
    if (!motionOn()) {
      this.paint(t);
      this.fill(t, false);
      return;
    }
    clearTimeout(this.readyTimer);
    this.gift.classList.add('is-ready');
    this.busy = true;
    this.gift.classList.add('is-out');
    this.cells.querySelectorAll('.nc-tile').forEach((tile) => tile.classList.add('is-leaving'));
    setTimeout(() => {
      this.paint(t);
      this.fill(t, true);
      void this.cells.offsetWidth;
      this.gift.classList.remove('is-out');
      requestAnimationFrame(() =>
        requestAnimationFrame(() => this.cells.querySelectorAll('.is-new').forEach((tile) => tile.classList.remove('is-new')))
      );
      setTimeout(() => {
        this.busy = false;
        if (this.pending) {
          const next = this.pending;
          this.pending = null;
          this.go(next);
        }
      }, 560);
    }, 270);
  }
}
if (!customElements.get('nc-turndown')) customElements.define('nc-turndown', NcTurndown);

/* ------------------------------------------------------------------ product grid chips */
class NcShop extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-chip]');
      if (!chip || !this.contains(chip)) return;
      const zone = chip.dataset.chip;
      if (zone) this.dataset.zone = zone;
      else delete this.dataset.zone;
      this.querySelectorAll('[data-chip]').forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
    });
  }
}
if (!customElements.get('nc-shop')) customElements.define('nc-shop', NcShop);

/* ------------------------------------------------------------------ clips */
class NcReel extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.button = this.querySelector('.nc-reel__play');
    if (!this.video || !this.button) return;
    this.button.addEventListener('click', () => (this.classList.contains('is-playing') ? this.stop() : this.play()));
    this.video.addEventListener('ended', () => this.stop());
  }

  play() {
    document.querySelectorAll('nc-reel.is-playing').forEach((r) => r !== this && r.stop && r.stop());
    this.video.muted = false;
    const started = this.video.play();
    this.classList.add('is-playing');
    this.label(true);
    if (started && started.catch) started.catch(() => this.stop());
  }

  stop() {
    this.video?.pause();
    this.classList.remove('is-playing');
    this.label(false);
  }

  label(playing) {
    const caption = this.querySelector('.nc-reel__cap strong')?.textContent || '';
    const word = playing ? this.button.dataset.labelPause : this.button.dataset.labelPlay;
    this.button.setAttribute('aria-label', `${word}: ${caption}`);
  }
}
if (!customElements.get('nc-reel')) customElements.define('nc-reel', NcReel);

/* ------------------------------------------------------------------ two-step sign-up */
class NcJoinSteps extends HTMLElement {
  connectedCallback() {
    this.form = this.closest('form');
    this.mail = this.querySelector('input[type="email"]');
    this.tags = this.querySelector('[data-join-tags]');
    this.error = this.querySelector('[data-join-error]');
    this.picked = new Set();
    this.querySelector('[data-join-next]')?.addEventListener('click', () => this.next());
    this.mail?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.next();
      }
    });
    this.querySelectorAll('[data-join-tag]').forEach((chip) =>
      chip.addEventListener('click', () => {
        const on = chip.getAttribute('aria-pressed') !== 'true';
        chip.setAttribute('aria-pressed', String(on));
        if (on) this.picked.add(chip.dataset.joinTag);
        else this.picked.delete(chip.dataset.joinTag);
        this.writeTags();
      })
    );
    this.querySelector('[data-join-skip]')?.addEventListener('click', () => {
      this.picked.clear();
      this.writeTags();
    });
    this.form?.addEventListener('submit', (event) => {
      if (!this.step(2).classList.contains('is-on')) {
        event.preventDefault();
        this.next();
      }
    });
  }

  step(n) {
    return this.querySelector(`[data-step="${n}"]`);
  }

  valid() {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((this.mail?.value || '').trim());
  }

  next() {
    if (!this.valid()) {
      if (this.error) this.error.hidden = false;
      this.mail?.setAttribute('aria-invalid', 'true');
      this.mail?.focus();
      return;
    }
    if (this.error) this.error.hidden = true;
    this.mail?.removeAttribute('aria-invalid');
    this.step(1).classList.remove('is-on');
    this.step(2).classList.add('is-on');
    this.step(2).querySelector('button')?.focus({ preventScroll: true });
  }

  writeTags() {
    if (!this.tags) return;
    this.tags.value = [this.tags.dataset.base, ...this.picked].filter(Boolean).join(', ');
  }
}
if (!customElements.get('nc-join-steps')) customElements.define('nc-join-steps', NcJoinSteps);

/* ------------------------------------------------------------------ copy a code */
class NcCopy extends HTMLElement {
  connectedCallback() {
    const button = this.querySelector('button');
    if (!button) return;
    button.addEventListener('click', () => {
      const text = this.dataset.copy || '';
      const was = button.textContent;
      const done = () => {
        button.textContent = this.dataset.done || was;
        setTimeout(() => (button.textContent = was), 1400);
      };
      if (navigator.clipboard) navigator.clipboard.writeText(text).then(done, done);
      else done();
    });
  }
}
if (!customElements.get('nc-copy')) customElements.define('nc-copy', NcCopy);

/* ------------------------------------------------------------------ collection sort: submit on change */
document.addEventListener('change', (event) => {
  const select = event.target.closest && event.target.closest('[data-nc-sort] select');
  if (select) select.form.submit();
});

/* the join form reloads the page after posting: bring the code into view */
if (window.location.search.includes('customer_posted=true')) {
  requestAnimationFrame(() => document.querySelector('.nc-join__done')?.focus({ preventScroll: false }));
}
